import { useEffect } from "react";
import { Form } from "react-bootstrap";
import { FieldValues, useFormContext, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "../../app/hook";
import { handleSetCloneExtra, wizardState } from "./botSlice";
import { ConnectForm } from "./BotWizard";
import CloneExternalLinks from "./CloneExternalLinks";
import { AIKittyType } from "./types";

const CloneExtraDescription = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { watch, setValue } = useFormContext();

  const wizard = useAppSelector(wizardState);

  const handleUpdateExternalLinks = (
    externalLinks: Array<{
      logo: string;
      link: string;
    }>
  ) => {
    setValue("externalLinks", externalLinks);

    handleUpdate();
  };

  const handleUpdate = () => {
    dispatch(
      handleSetCloneExtra({
        description: {
          name: watch("name"),
          profession: watch("profession"),
          bio: watch("bio")
        },
        externalLinks: watch("externalLinks") ?? []
      })
    );
  };

  useEffect(() => {
    if ((wizard.bot_config as AIKittyType).cloneExtra?.externalLinks)
      setValue(
        "externalLinks",
        (wizard.bot_config as AIKittyType).cloneExtra?.externalLinks
      );
  }, []);

  return (
    <ConnectForm>
      {({
        register,
        formState: { errors }
      }: UseFormReturn<FieldValues, any>) => (
        <div className="md:tw-px-8 tw-px-1 tw-flex-col tw-justify-start tw-tw-items-start tw-inline-flex md:tw-w-2/3 tw-w-full">
          <div className="tw-flex tw-flex-col tw-gap-y-6">
            <div className="description-infos">
              <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-8 tw-flex tw-mb-8">
                <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex">
                  <div className="tw-self-stretch tw-text-gray-900 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">
                    {t("Wizard/CloneExtraDescription/key1")}
                  </div>
                  <div className="tw-self-stretch tw-text-gray-900 text-sm font-normal tw-font-['Lexend'] tw-leading-tight">
                    {t("Wizard/CloneExtraDescription/key2")}
                  </div>
                </div>
              </div>

              <Form.Group className="tw-mb-3">
                <Form.Label>
                  {t("Wizard/CloneExtraDescription/key3")}
                </Form.Label>
                <Form.Control
                  placeholder={t(
                    "Wizard/CloneExtraDescription/key4"
                  ).toString()}
                  isInvalid={!!errors.name}
                  defaultValue={
                    (wizard.bot_config as AIKittyType).cloneExtra?.data?.name ??
                    ""
                  }
                  {...register("name", { onChange: handleUpdate })}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name?.message as string}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="tw-mb-3">
                <Form.Label>
                  {t("Wizard/CloneExtraDescription/key5")}
                </Form.Label>
                <Form.Control
                  placeholder={t(
                    "Wizard/CloneExtraDescription/key6"
                  ).toString()}
                  isInvalid={!!errors.profession}
                  defaultValue={
                    (wizard.bot_config as AIKittyType).cloneExtra?.data
                      ?.profession ?? ""
                  }
                  {...register("profession", { onChange: handleUpdate })}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.profession?.message as string}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="tw-mb-3">
                <Form.Label>
                  {t("Wizard/CloneExtraDescription/key7")}
                </Form.Label>
                <Form.Control
                  as={"textarea"}
                  rows={4}
                  placeholder={t(
                    "Wizard/CloneExtraDescription/key8"
                  ).toString()}
                  isInvalid={!!errors.bio}
                  defaultValue={
                    (wizard.bot_config as AIKittyType).cloneExtra?.data?.bio ??
                    ""
                  }
                  {...register("bio", { onChange: handleUpdate })}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.bio?.message as string}
                </Form.Control.Feedback>
              </Form.Group>
            </div>

            <div className="external-links">
              <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-8 tw-flex tw-mb-8">
                <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex">
                  <div className="tw-self-stretch tw-text-gray-900 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">
                    {t("Wizard/CloneExtraDescription/key9")}
                  </div>
                  <div className="tw-self-stretch tw-text-gray-900 text-sm font-normal tw-font-['Lexend'] tw-leading-tight">
                    {t("Wizard/CloneExtraDescription/key10")}
                  </div>
                </div>
              </div>

              <CloneExternalLinks
                initialData={
                  (wizard.bot_config as AIKittyType).cloneExtra
                    ?.externalLinks ?? []
                }
                onChange={handleUpdateExternalLinks}
              />

              {!!errors.externalLinks && (
                <span className="tw-text-sm tw-text-red-600 tw-mt-2 tw-block">
                  {errors.externalLinks?.message as string}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </ConnectForm>
  );
};

export default CloneExtraDescription;
